import { ReactNode } from "react";
import { Box, Typography } from "@mui/material";

export interface Segment {
  label: string;
  percent: number;
  color: string;
  /** optional loan count shown in the legend */
  count?: number;
}

/**
 * One legend row — Figma Text_Input_Type (node 6235:33494): 24px swatch box
 * holding a 12×12 rounded square, semibold label, then "count (percent)".
 */
export const LegendRow = ({
  color,
  label,
  value,
}: {
  color: string;
  label: string;
  value?: ReactNode;
}) => (
  <Box display="flex" alignItems="center" gap="8px" height={24} width="100%">
    <Box
      width={24}
      height={24}
      flexShrink={0}
      display="flex"
      alignItems="center"
      justifyContent="center"
    >
      <Box width={12} height={12} borderRadius="2px" bgcolor={color} />
    </Box>
    <Typography
      fontSize="12px"
      fontWeight={600}
      lineHeight="16px"
      color="var(--dark)"
      flex={1}
      minWidth={0}
      noWrap
    >
      {label}
    </Typography>
    {value}
  </Box>
);

interface Props {
  segments: Segment[];
  /** show the % text inside each bar segment (default true) */
  showBarLabels?: boolean;
  /**
   * Smallest segment percent that still shows its in-bar label. Narrow slices
   * can't fit the text, so they're hidden (matches Figma, which hides the
   * 2.8%/1.4% coverage slices). Lower it for the DQS bar so its 5% slice keeps
   * a label like the Figma Segment_Button.
   */
  minLabelPercent?: number;
  /** what to show as the legend value (default "percent") */
  legendValue?: "percent" | "count-percent" | "count" | "none";
}

/** Horizontal stacked percentage bar with a row legend — Figma DQS 7053:14403 / Coverage 7053:14415. */
const SegmentedBar = ({
  segments,
  showBarLabels = true,
  minLabelPercent = 8,
  legendValue = "percent",
}: Props) => {
  const valueText = (s: Segment) => {
    if (legendValue === "none") return null;
    return (
      <Box display="flex" alignItems="center" gap="8px" flexShrink={0}>
        {legendValue !== "percent" && s.count != null && (
          <Typography
            fontSize="12px"
            fontWeight={600}
            lineHeight="16px"
            color="var(--dark)"
          >
            {s.count}
          </Typography>
        )}
        {legendValue !== "count" && (
          <Typography fontSize="12px" lineHeight="16px" color="var(--gray1)">
            ({s.percent}%)
          </Typography>
        )}
      </Box>
    );
  };

  return (
    <Box>
      <Box
        display="flex"
        width="100%"
        // Figma Segment_Button / Stat_chart (7812:17002, 7053:14419): 52px, radius 8
        height="52px"
        borderRadius="8px"
        overflow="hidden"
        bgcolor="var(--gray6)"
      >
        {segments.map((s, i) => (
          <Box
            key={`${i}-${s.label}`}
            width={`${s.percent}%`}
            bgcolor={s.color}
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            {showBarLabels && s.percent >= minLabelPercent && (
              <Typography
                fontSize="12px"
                fontWeight={600}
                lineHeight="17.16px"
                color="var(--white)"
              >
                {s.percent}%
              </Typography>
            )}
          </Box>
        ))}
      </Box>

      <Box display="flex" flexDirection="column" gap="4px" mt="16px">
        {segments.map((s, i) => (
          <LegendRow
            key={`${i}-${s.label}`}
            color={s.color}
            label={s.label}
            value={valueText(s)}
          />
        ))}
      </Box>
    </Box>
  );
};

export default SegmentedBar;
